import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import type {
  SendTaskReminderDto,
  TaskListResponseDto,
  TaskReminderResponseDto,
  TaskStatus,
  TaskType,
} from '@monorepo/shared';
import { apiClient } from '../lib/axios';
import { queryClient } from '../lib/queryClient';

export type TaskScopeTab = 'mine' | 'all';

export interface TaskQueryParams {
  type?: TaskType | undefined;
  status?: TaskStatus | undefined;
  userCompanyId?: string | undefined;
  search?: string | undefined;
  page: number;
  limit: number;
}

const tasksApi = {
  getTasks: async (
    companyId: string,
    scope: TaskScopeTab,
    params: TaskQueryParams,
  ): Promise<TaskListResponseDto> => {
    const path =
      scope === 'all' ? `/companies/${companyId}/tasks/all` : `/companies/${companyId}/tasks`;
    const { data: response } = await apiClient.get<TaskListResponseDto>(path, {
      params,
    });
    return response;
  },

  sendReminder: async (
    companyId: string,
    data: SendTaskReminderDto,
  ): Promise<TaskReminderResponseDto> => {
    const { data: response } = await apiClient.post<TaskReminderResponseDto>(
      `/companies/${companyId}/tasks/reminders`,
      data,
    );
    return response;
  },
};

export const useGetTasks = (
  companyId: string,
  scope: TaskScopeTab,
  params: TaskQueryParams,
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      'tasks',
      companyId,
      scope,
      params.type ?? null,
      params.status ?? null,
      params.userCompanyId ?? null,
      params.search ?? null,
      params.page,
      params.limit,
    ],
    queryFn: () => tasksApi.getTasks(companyId, scope, params),
    enabled: !!companyId && enabled,
  });
};

export const useSendTaskReminder = (companyId: string) => {
  return useMutation({
    mutationFn: (taskRef: string) => tasksApi.sendReminder(companyId, { taskRef }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', companyId] });
      if (result.sent) {
        toast.success(i18next.t('tasks.reminder.success'));
      } else {
        toast.error(i18next.t('tasks.reminder.unavailable'));
      }
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};
